import React, { useCallback, useEffect, useMemo } from 'react';
import { equals, remove, props } from 'ramda';

import TextInput from 'components/TextInputNew';
import Button from 'components/Button';
import Table from 'components/Table';
import Message from 'components/Message';
import {
  EMPTY,
  CUSTOM,
  STATUS,
  CURSOR,
  COUNT,
  LIMIT,
  HAS_MORE,
  ID,
  useInitReports,
  useReportsActions,
  useAlerts,
} from 'store/reports';
import { REPORT, usePermissions } from 'permissions';

import Row from './Row';
import DropDown from './DropDown';
import { Container, Wrapper } from './styles';
import { headerFields, handleFields } from './utils';
import { useCheckedList } from './hooks';

const ExternReports = () => {
  const can = usePermissions();
  const { list: reports, search, sort, pagination, loading: reportsLoading } = useInitReports(can.read(REPORT));

  const { updateSearch, updatePagination, updateSort, updateReports, saveExternReport, createReportsArchive } =
    useReportsActions();
  const { list, checkboxStatus, setCheckboxStatus, markers, setMarkers } = useCheckedList(reports);
  const { action: createArchive, loading, success, error, resetAlerts } = useAlerts(createReportsArchive);
  const { action: saveReportExtract, success: saveReportSuccess } = useAlerts(saveExternReport);

  const handleCheckBoxClick = useCallback(
    async (reportId) => {
      if (!reportId) return;
      const index = markers.findIndex(equals(reportId));
      const nextState = index < 0 ? [...markers, reportId] : remove(index, 1)(markers);

      setMarkers(nextState);
      setCheckboxStatus(nextState.length ? CUSTOM : EMPTY);
    },
    [markers, setMarkers, setCheckboxStatus]
  );

  const updateReportStatus = useCallback(
    ({ reportId, status }) => {
      if (status !== 2) saveReportExtract({ reportId, status: 2 });
    },
    [saveReportExtract]
  );

  const handleArchiveClick = useCallback(() => {
    if (!checkboxStatus || checkboxStatus === EMPTY || loading) return;

    if (checkboxStatus === CUSTOM) {
      createArchive({
        filter: CUSTOM,
        reportIds: markers,
        ...(search && { search }),
      });
    } else {
      createArchive({ filter: checkboxStatus, ...(search && { search }) });
    }
  }, [checkboxStatus, createArchive, loading, markers, search]);

  const renderRow = useCallback(
    (args) => (
      <Row
        markers={markers}
        onCheckBoxClick={handleCheckBoxClick}
        updateReportStatus={updateReportStatus}
        {...handleFields(args)}
      />
    ),
    [handleCheckBoxClick, updateReportStatus, markers]
  );

  const errorMessage = useMemo(() => {
    if (error) return error;
    if (!markers.length) return 'Bitte wählen Sie die Berichte aus, die Sie herunterladen möchten.';

    return null;
  }, [error, markers.length]);

  useEffect(() => {
    if (success) {
      updateReports(
        reports.map((report) => {
          const [id, status] = props([ID, STATUS], report);
          if (status !== 2 && markers.includes(id)) {
            return { ...report, [STATUS]: 2 };
          }

          return report;
        })
      );
      setMarkers([]);
      resetAlerts();
    }
  }, [markers, reports, resetAlerts, setMarkers, success, updateReports]);

  return (
    <Container>
      <TextInput type="search" placeholder="Bericht suchen" value={search} handleChangeText={updateSearch} mb={70} />
      <Wrapper>
        <Button width="260px" color="normal" onClick={handleArchiveClick} disabled={!markers.length || loading}>
          markierte berichte herunterladen
        </Button>
        {errorMessage && <Message type="error">{errorMessage}</Message>}
      </Wrapper>
      <Table
        title="Berichte"
        head={headerFields(
          <DropDown
            marked={markers.length}
            all={list.length}
            checkboxStatus={checkboxStatus}
            setCheckboxStatus={setCheckboxStatus}
            saveSuccess={Boolean(saveReportSuccess)}
            archiveSuccess={Boolean(success)}
          />
        )}
        list={list}
        renderRow={renderRow}
        sort={sort}
        cursor={pagination[CURSOR]}
        count={pagination[COUNT]}
        limit={pagination[LIMIT]}
        hasMore={pagination[HAS_MORE]}
        updateSort={updateSort}
        updatePagination={updatePagination}
        loading={reportsLoading}
      />
    </Container>
  );
};

export default ExternReports;
